const CART_COUNTDOWN_STORAGE_KEY = 'cartCountdownExpiresAt';
const CART_COUNTDOWN_EXPIRED_KEY = 'cartCountdownExpired';

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.onWheelPrevent = this.onWheelPrevent.bind(this);
    this.onTouchMovePrevent = this.onTouchMovePrevent.bind(this);
    this.onKeydownPrevent = this.onKeydownPrevent.bind(this);
    this.countdownExpired = false;
  }

  isDesktopProductPage() {
    return (
      window.matchMedia('(min-width: 750px)').matches &&
      document.body.classList.contains('template-product')
    );
  }

  isScrollInsideDrawer(target) {
    const inner = this.querySelector('.drawer__inner');
    return !!(inner && target && inner.contains(target));
  }

  onWheelPrevent(event) {
    if (!this.classList.contains('active')) return;
    if (this.isScrollInsideDrawer(event.target)) return;
    event.preventDefault();
  }

  onTouchMovePrevent(event) {
    if (!this.classList.contains('active')) return;
    if (this.isScrollInsideDrawer(event.target)) return;
    event.preventDefault();
  }

  onKeydownPrevent(event) {
    if (!this.classList.contains('active')) return;
    if (this.isScrollInsideDrawer(document.activeElement)) return;

    const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
    if (scrollKeys.includes(event.key)) event.preventDefault();
  }

  bindScrollPreventers() {
    document.addEventListener('wheel', this.onWheelPrevent, { passive: false });
    document.addEventListener('touchmove', this.onTouchMovePrevent, { passive: false });
    document.addEventListener('keydown', this.onKeydownPrevent);
  }

  unbindScrollPreventers() {
    document.removeEventListener('wheel', this.onWheelPrevent);
    document.removeEventListener('touchmove', this.onTouchMovePrevent);
    document.removeEventListener('keydown', this.onKeydownPrevent);
  }

  connectedCallback() {
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindEvents();
    this.setHeaderCartIconAccessibility();

    if (this.classList.contains('is-empty')) {
      CartDrawer.clearCountdownState();
    } else if (this.querySelector('[data-cart-drawer-countdown]')) {
      this.prepareCountdown();
    }
  }

  static getCountdownDurationSeconds(banner) {
    return parseInt(banner?.dataset?.countdownStart || '10', 10) * 60;
  }

  static isCountdownExpired() {
    try {
      return sessionStorage.getItem(CART_COUNTDOWN_EXPIRED_KEY) === '1';
    } catch {
      return false;
    }
  }

  static setCountdownExpired() {
    try {
      sessionStorage.setItem(CART_COUNTDOWN_EXPIRED_KEY, '1');
      sessionStorage.removeItem(CART_COUNTDOWN_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
  }

  static clearCountdownExpired() {
    try {
      sessionStorage.removeItem(CART_COUNTDOWN_EXPIRED_KEY);
    } catch {
      // ignore storage errors
    }
  }

  static getStoredCountdownRemaining() {
    try {
      const expiresAt = parseInt(sessionStorage.getItem(CART_COUNTDOWN_STORAGE_KEY), 10);
      if (!Number.isFinite(expiresAt)) return null;
      return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
    } catch {
      return null;
    }
  }

  static setCountdownExpiry(seconds) {
    try {
      if (seconds <= 0) {
        sessionStorage.removeItem(CART_COUNTDOWN_STORAGE_KEY);
        return;
      }
      sessionStorage.setItem(CART_COUNTDOWN_STORAGE_KEY, String(Date.now() + seconds * 1000));
    } catch {
      // ignore storage errors
    }
  }

  static clearCountdownExpiry() {
    try {
      sessionStorage.removeItem(CART_COUNTDOWN_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
  }

  static clearCountdownState() {
    CartDrawer.clearCountdownExpiry();
    CartDrawer.clearCountdownExpired();
  }

  resolveCountdownRemaining(banner, reset) {
    const duration = CartDrawer.getCountdownDurationSeconds(banner);

    if (reset) {
      CartDrawer.clearCountdownExpired();
      CartDrawer.setCountdownExpiry(duration);
      return duration;
    }

    if (this.countdownTimeRemaining != null) {
      return this.countdownTimeRemaining;
    }

    const stored = CartDrawer.getStoredCountdownRemaining();
    if (stored !== null) {
      return stored;
    }

    CartDrawer.setCountdownExpiry(duration);
    return duration;
  }

  setCountdownView(banner, state) {
    if (!banner) return;

    banner.classList.toggle('is-expired', state === 'expired');

    const active = banner.querySelector('[data-cart-countdown-active]');
    const expired = banner.querySelector('[data-cart-countdown-expired]');

    if (active) active.hidden = state === 'expired';
    if (expired) expired.hidden = state !== 'expired';
  }

  handleCountdownExpired(banner) {
    this.stopCountdown();
    this.countdownTimeRemaining = 0;
    this.countdownExpired = true;
    CartDrawer.setCountdownExpired();
    this.setCountdownView(banner, 'expired');
  }

  shouldResetCountdown() {
    return CartDrawer.isCountdownExpired() || this.countdownExpired;
  }

  prepareCountdown() {
    if (this.shouldResetCountdown()) {
      CartDrawer.clearCountdownExpired();
      this.countdownExpired = false;
      this.startCountdown({ reset: true });
      return;
    }

    this.startCountdown({ reset: false });
  }

  bindEvents() {
    this.removeEventListener('click', this.handleCloseClick);
    this.addEventListener('click', this.handleCloseClick);

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.removeEventListener('click', this.handleOverlayClick);
      overlay.addEventListener('click', this.handleOverlayClick);
    }
  }

  handleCloseClick(event) {
    if (event.target.closest('.drawer__close')) {
      event.preventDefault();
      this.close();
    }
  }

  handleOverlayClick(event) {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    // On the cart page itself, leave the header cart icon as a plain link to
    // /cart so clicking it stays on the page instead of opening the drawer.
    if (document.body.classList.contains('template-cart')) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  formatCountdownTime(timeRemaining) {
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    return minutes + ':' + (seconds < 10 ? '0' + seconds : seconds);
  }

  syncCountdownDisplay() {
    const banner = this.querySelector('[data-cart-drawer-countdown]');
    if (!banner) return;

    if (this.countdownExpired) {
      this.setCountdownView(banner, 'expired');
      return;
    }

    const timerEl = banner.querySelector('[data-cart-countdown-timer]');
    if (!timerEl || this.countdownTimeRemaining == null) return;
    timerEl.textContent = this.formatCountdownTime(this.countdownTimeRemaining);
  }

  startCountdown({ reset = false } = {}) {
    const banner = this.querySelector('[data-cart-drawer-countdown]');
    if (!banner) {
      this.stopCountdown();
      this.countdownTimeRemaining = null;
      this.countdownExpired = false;
      CartDrawer.clearCountdownState();
      return;
    }

    if (this.countdownTimeout && !reset) {
      this.syncCountdownDisplay();
      return;
    }

    if (reset) {
      this.stopCountdown();
      this.countdownExpired = false;
      this.setCountdownView(banner, 'active');
    }

    this.countdownTimeRemaining = this.resolveCountdownRemaining(banner, reset);

    if (this.countdownTimeRemaining === 0) {
      this.handleCountdownExpired(banner);
      return;
    }

    const updateCountdown = () => {
      const timerEl = this.querySelector('[data-cart-countdown-timer]');
      if (!timerEl) {
        this.stopCountdown();
        return;
      }

      timerEl.textContent = this.formatCountdownTime(this.countdownTimeRemaining);

      if (this.countdownTimeRemaining > 0) {
        this.countdownTimeRemaining -= 1;
        this.countdownTimeout = window.setTimeout(updateCountdown, 1000);
      } else {
        this.handleCountdownExpired(banner);
      }
    };

    updateCountdown();
  }

  stopCountdown() {
    if (this.countdownTimeout) {
      clearTimeout(this.countdownTimeout);
      this.countdownTimeout = null;
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    if (!this.isDesktopProductPage()) {
      document.body.classList.add('overflow-hidden');
    }
    this.lockPageScroll();

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.style.removeProperty('visibility');
    this.style.removeProperty('pointer-events');

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.prepareCountdown();

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
  }

  lockPageScroll() {
    if (this.pageScrollLocked) return;

    this.pageScrollLocked = true;
    this.scrollPosition = window.scrollY || document.documentElement.scrollTop || 0;

    // Desktop PDP: avoid overflow/fixed body (breaks sticky gallery); block scroll via events only.
    if (this.isDesktopProductPage()) {
      this.useSoftScrollLock = true;
      this.bindScrollPreventers();
      return;
    }

    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.documentElement.style.setProperty('--cart-drawer-scrollbar-width', `${scrollbarWidth}px`);
    document.documentElement.classList.add('cart-drawer-scroll-lock');

    this.useFixedBodyScrollLock = window.matchMedia('(max-width: 749px)').matches;

    if (this.useFixedBodyScrollLock) {
      document.body.classList.add('cart-drawer-scroll-lock');
      document.body.style.top = `-${this.scrollPosition}px`;
    } else {
      document.body.classList.add('cart-drawer-scroll-lock-body');
    }
  }

  unlockPageScroll() {
    if (!this.pageScrollLocked) return;

    if (this.useSoftScrollLock) {
      this.unbindScrollPreventers();
      this.useSoftScrollLock = false;
      this.pageScrollLocked = false;
      return;
    }

    const scrollY = this.scrollPosition || 0;
    const usedFixedBody = this.useFixedBodyScrollLock;
    this.pageScrollLocked = false;
    this.useFixedBodyScrollLock = false;

    document.documentElement.classList.remove('cart-drawer-scroll-lock');
    document.documentElement.style.removeProperty('--cart-drawer-scrollbar-width');
    document.body.classList.remove('cart-drawer-scroll-lock', 'cart-drawer-scroll-lock-body');
    document.body.style.top = '';

    if (usedFixedBody) {
      window.scrollTo(0, scrollY);
    }
  }

  close() {
    this.classList.remove('active', 'animate');
    removeTrapFocus(this.activeElement);
    this.unlockPageScroll();
    document.body.classList.remove('overflow-hidden');

    // iOS can leave the backdrop visible when visibility is transitioned — hide immediately.
    this.style.visibility = 'hidden';
    this.style.pointerEvents = 'none';
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const wasEmpty = this.classList.contains('is-empty');
    this.classList.contains('is-empty') && this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.bindEvents();

    if (this.shouldResetCountdown()) {
      CartDrawer.clearCountdownExpired();
      this.countdownExpired = false;
      this.startCountdown({ reset: true });
    } else {
      this.startCountdown({ reset: wasEmpty });
    }

    this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
